import { useState } from "react";
import { Phone, MessageCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { CONTACT } from "../siteConfig";

const INDUSTRIES = ["Mining", "Earthmoving", "Construction", "Commercial Transport", "Industrial", "Fleet Operations", "Other"];

const EMPTY = {
  name: "", company: "", phone: "", email: "", location: "",
  industry: "", equipment: "", tyre_size: "", requirement: "", message: "",
};

const inputCls =
  "w-full bg-obsidian-3 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:border-ember focus:outline-none transition-colors";

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
}

export default function EnquiryForm({ compact = false }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent — our team will reach out shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="enquiry-form">
      <form onSubmit={submit} className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <input data-testid="enquiry-name-input" required placeholder="Name *" value={form.name} onChange={set("name")} className={inputCls} />
        <input data-testid="enquiry-company-input" placeholder="Company" value={form.company} onChange={set("company")} className={inputCls} />
        <input data-testid="enquiry-phone-input" required placeholder="Phone *" value={form.phone} onChange={set("phone")} className={inputCls} />
        <input data-testid="enquiry-email-input" type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
        <input data-testid="enquiry-location-input" placeholder="Location" value={form.location} onChange={set("location")} className={inputCls} />
        <select data-testid="enquiry-industry-select" value={form.industry} onChange={set("industry")} className={inputCls}>
          <option value="">Industry</option>
          {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
        <input data-testid="enquiry-equipment-input" placeholder="Equipment (e.g. Loader, Dumper)" value={form.equipment} onChange={set("equipment")} className={inputCls} />
        <input data-testid="enquiry-size-input" placeholder="Tyre Size (e.g. 11.00R20)" value={form.tyre_size} onChange={set("tyre_size")} className={inputCls} />
        <input data-testid="enquiry-requirement-input" placeholder="Requirement" value={form.requirement} onChange={set("requirement")} className={`${inputCls} sm:col-span-2`} />
        <textarea data-testid="enquiry-message-input" placeholder="Message" rows={4} value={form.message} onChange={set("message")} className={`${inputCls} sm:col-span-2 resize-none`} />
        <div className="sm:col-span-2 flex flex-wrap items-center gap-3 pt-2">
          <button
            type="submit"
            data-testid="enquiry-submit-button"
            disabled={sending}
            className="inline-flex items-center gap-2 bg-ember px-7 py-3.5 font-mono text-[11px] tracking-[0.22em] text-[#10141B] transition-colors hover:bg-ember-hover disabled:opacity-60"
          >
            {sending ? "SENDING…" : "SEND ENQUIRY"} <ArrowRight className="h-4 w-4" />
          </button>
          <a
            href={CONTACT.phoneHref}
            data-testid="enquiry-call-button"
            className="inline-flex items-center gap-2 border border-white/20 px-7 py-3.5 font-mono text-[11px] tracking-[0.22em] text-white transition-colors hover:border-ember hover:text-ember"
          >
            <Phone className="h-4 w-4" /> CALL US
          </a>
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="enquiry-whatsapp-button"
            className="inline-flex items-center gap-2 border border-white/20 px-7 py-3.5 font-mono text-[11px] tracking-[0.22em] text-white transition-colors hover:border-ember hover:text-ember"
          >
            <MessageCircle className="h-4 w-4" /> WHATSAPP US
          </a>
        </div>
      </form>
    </div>
  );
}
